import { gql } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import {
  EditScheduleModal_ScheduleFragment,
  useScheduleEditMutation,
} from './__generated__/EditScheduleModal.graphql';

interface Props {
  schedule: EditScheduleModal_ScheduleFragment;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (update: EditScheduleModal_ScheduleFragment) => Promise<void> | void;
}

const schema = z.object({
  scheduleId: z.string(),
  title: z.string(),
  description: z.string().nullish(),
});

type FormValues = z.infer<typeof schema>;

export function EditScheduleModal({ isOpen, onCancel, onSuccess, schedule }: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  return (
    <Modal isOpen={isOpen} onClose={onCancel} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <EditScheduleModalContent
          schedule={schedule}
          initialFocusRef={initialFocusRef}
          onCancel={onCancel}
          onSuccess={onSuccess}
        />
      </ModalContent>
    </Modal>
  );
}

type ContentProps = {
  schedule: EditScheduleModal_ScheduleFragment;
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  onCancel: () => void;
  onSuccess: (update: EditScheduleModal_ScheduleFragment) => Promise<void> | void;
};

function EditScheduleModalContent({
  onSuccess,
  onCancel,
  initialFocusRef,
  schedule,
}: ContentProps) {
  const {
    handleSubmit,
    reset,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      scheduleId: schedule.id,
      title: schedule.title,
      description: schedule.description,
    },
    resolver: zodResolver(schema),
  });
  const toast = useToast();
  const [editSchedule] = useScheduleEditMutation();
  const { ref: titleInputRef, ...titleInputProps } = register('title');
  const { ref: descriptionInputRef, ...descriptionInputProps } = register('description');
  const analytics = useAnalyticsReporter();

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const { data } = await editSchedule({
          variables: {
            input: {
              scheduleId: values.scheduleId,
              title: values.title,
              description: values.description,
            },
          },
        });

        if (!data) {
          throw new Error('no data received');
        }

        analytics.track('scheduleUpdate');
        await onSuccess?.(data.scheduleUpdate.schedule);
        reset();

        toast({
          status: 'success',
          title: t('scheduleEdited'),
          description: t('scheduleEditedDesc'),
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('scheduleEditErr'),
          description: fromError(err, 'EditSchedule'),
        });
      }
    },
    [analytics, editSchedule, onSuccess, reset, toast],
  );

  const handleClose = useCallback(() => {
    reset();
    onCancel();
  }, [reset, onCancel]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>{t('editSchedule')}</ModalHeader>
      <ModalCloseButton tabIndex={5} />

      <ModalBody>
        <VStack spacing="4">
          <FormControl isInvalid={Boolean(errors.title)} marginBottom={3}>
            <FormLabel>{t('scheduleTitle')}</FormLabel>
            <Input
              placeholder={t('schedulePlaceholder')}
              ref={(r) => {
                titleInputRef(r);
                initialFocusRef.current = r;
              }}
              {...titleInputProps}
            />
            <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.description)}>
            <FormLabel>{t('description')}</FormLabel>
            <Input
              placeholder={t('someDescription')}
              ref={(r) => {
                descriptionInputRef(r);
                initialFocusRef.current = r;
              }}
              {...descriptionInputProps}
            />
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleClose} variant="ghost" colorScheme="blue" isDisabled={isSubmitting}>
          {t('cancel')}
        </Button>
        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          type="submit"
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
        >
          {t('save')}
        </Button>
      </ModalFooter>
    </form>
  );
}

EditScheduleModal.graphql = {
  fragments: {
    EditScheduleModal_schedule: gql`
      fragment EditScheduleModal_schedule on Schedule {
        id
        title
        description
      }
    `,
  },
  mutations: {
    ScheduleEdit: gql`
      mutation ScheduleEdit($input: ScheduleUpdateInput!) {
        scheduleUpdate(input: $input) {
          schedule {
            id
            ...EditScheduleModal_schedule
          }
        }
      }
    `,
  },
};
